import { Button } from '@shared/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/use-toast';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDefaultShippingCosts,
  useDeleteDefaultShippingCost,
  useProfitabilityCourierServiceOptions,
} from '../../hooks';
import type { ProfitabilityDefaultShippingCost } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { getProfitabilityMutationErrorMessage } from '../../utils/profitabilityApiError';

interface DefaultShippingCostsPanelProps {
  locale: string;
  onEdit: (row: ProfitabilityDefaultShippingCost, serviceLabel: string) => void;
  onAdd: () => void;
}

export const DefaultShippingCostsPanel = ({ locale, onEdit, onAdd }: DefaultShippingCostsPanelProps) => {
  const { t } = useTranslation('profitabilityModule');
  const { toast } = useToast();
  const currency = useCurrency();

  const listQuery = useDefaultShippingCosts(true);
  const courierOptionsQ = useProfitabilityCourierServiceOptions();
  const deleteM = useDeleteDefaultShippingCost();

  const [pendingDelete, setPendingDelete] = useState<ProfitabilityDefaultShippingCost | null>(null);

  const courierLabelById = useMemo(() => {
    const m = new Map<string, string>();
    (courierOptionsQ.data ?? []).forEach((o) => m.set(o.value, o.label));
    return m;
  }, [courierOptionsQ.data]);

  const formatPercent = useCallback(
    (n: number) =>
      `${new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)} %`,
    [locale],
  );

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteM.mutateAsync(pendingDelete.id);
      toast({ title: t('default_shipping_costs.toast.deleted') });
      setPendingDelete(null);
    } catch (err) {
      toast({
        title: t('default_shipping_costs.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  const rows = listQuery.data?.data ?? [];

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          variant="outline"
          className="gap-2 border-border"
          onClick={onAdd}
          data-testid="profitability-default-shipping-add"
        >
          <Plus className="h-4 w-4" />
          {t('default_shipping_costs.add')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">{t('default_shipping_costs.col_service')}</TableHead>
              <TableHead className="text-right text-muted-foreground">{t('default_shipping_costs.col_cost')}</TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('default_shipping_costs.col_cod_fee_fixed')}
              </TableHead>
              <TableHead className="text-right text-muted-foreground">
                {t('default_shipping_costs.col_cod_fee_percent')}
              </TableHead>
              <TableHead className="w-[100px] text-muted-foreground">
                {t('default_shipping_costs.col_actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  …
                </TableCell>
              </TableRow>
            )}
            {listQuery.isError && !listQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-destructive">
                  {t('default_shipping_costs.list_error')}
                </TableCell>
              </TableRow>
            )}
            {!listQuery.isLoading && !listQuery.isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {t('default_shipping_costs.empty')}
                </TableCell>
              </TableRow>
            )}
            {!listQuery.isLoading &&
              !listQuery.isError &&
              rows.map((row) => {
                const serviceLabel =
                  courierLabelById.get(String(row.courier_service_id)) ?? `#${row.courier_service_id}`;
                return (
                  <TableRow
                    key={row.id}
                    className="border-border"
                    data-testid={`profitability-default-shipping-row-${row.id}`}
                  >
                    <TableCell className="max-w-[280px] truncate text-sm text-foreground">{serviceLabel}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatProfitabilityAmount(row.cost, locale, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatProfitabilityAmount(row.cod_fee_fixed ?? 0, locale, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatPercent(row.cod_fee_percent ?? 0)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onEdit(row, serviceLabel)}
                          title={t('default_shipping_costs.edit')}
                          data-testid={`profitability-default-shipping-edit-${row.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setPendingDelete(row)}
                          title={t('default_shipping_costs.delete')}
                          data-testid={`profitability-default-shipping-delete-${row.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <DialogContent className="max-w-md border-border bg-background">
          <DialogHeader>
            <DialogTitle>{t('default_shipping_costs.delete_confirm_title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('default_shipping_costs.delete_confirm_body')}</p>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPendingDelete(null)} className="border-border">
              {t('default_shipping_costs.delete_cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={confirmDelete}
              data-testid="profitability-default-shipping-confirm-delete"
            >
              {t('default_shipping_costs.delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
